import React, { useEffect, useState, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { getDashboardStats, getLowStockAlerts, getBranches } from '../lib/api';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { DollarSign, Receipt, AlertTriangle, Scissors, Clock, Building2, Tag } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from 'recharts';

const STATUS_LABELS = {
  PENDIENTE_PAGO:     'Pendiente de pago',
  PRE_PRODUCCION:     'Pre-producción',
  EN_PRODUCCION:      'En producción',
  CONFECCIONADO:      'Confeccionado',
  LISTO_PARA_ENTREGA: 'Listo para entrega',
};

const STATUS_COLORS = {
  PENDIENTE_PAGO:     'bg-yellow-100 text-yellow-800',
  PRE_PRODUCCION:     'bg-blue-100 text-blue-800',
  EN_PRODUCCION:      'bg-indigo-100 text-indigo-800',
  CONFECCIONADO:      'bg-purple-100 text-purple-800',
  LISTO_PARA_ENTREGA: 'bg-green-100 text-green-800',
};

const money = (n) => `RD$${parseFloat(n || 0).toLocaleString('es-DO', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const StatCard = ({ title, value, subtitle, icon: Icon, iconColor = 'text-cyan-500', valueColor = '', testId, compact = false }) => (
  <Card className="bg-white border border-slate-200 shadow-sm hover:shadow-md transition-shadow duration-200" data-testid={testId}>
    <CardHeader className="flex flex-row items-center justify-between pb-2">
      <CardTitle className="text-sm font-medium text-slate-600">{title}</CardTitle>
      <Icon className={`w-5 h-5 ${iconColor}`} />
    </CardHeader>
    <CardContent>
      <div className={`font-bold ${compact ? 'text-xl' : 'text-3xl'} ${valueColor}`} style={{ fontFamily: 'JetBrains Mono' }}>
        {value}
      </div>
      {subtitle && <p className="text-xs text-slate-500 mt-1">{subtitle}</p>}
    </CardContent>
  </Card>
);

const Dashboard = () => {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';

  const [stats, setStats] = useState(null);
  const [lowStockAlerts, setLowStockAlerts] = useState([]);
  const [lowStockLoaded, setLowStockLoaded] = useState(false);
  const [loadingStock, setLoadingStock] = useState(false);
  const [branches, setBranches] = useState([]);
  const [selectedBranch, setSelectedBranch] = useState(() =>
    isAdmin ? (localStorage.getItem('dashboard_branch') || '') : (user?.branch_id || '')
  );
  const [loading, setLoading] = useState(true);

  // Load branches for name resolution (admin: selector, vendedor: name display)
  useEffect(() => {
    getBranches().then(b => setBranches(b || [])).catch(() => {});
  }, []);

  const loadData = useCallback(async (branchId) => {
    setLoading(true);
    try {
      const statsData = await getDashboardStats(branchId);
      setStats(statsData);
    } catch {
      toast.error('Error al cargar datos del dashboard');
    } finally {
      setLoading(false);
    }
  }, []);

  const handleLoadStock = async () => {
    setLoadingStock(true);
    try {
      const alertsData = await getLowStockAlerts();
      setLowStockAlerts(alertsData);
      setLowStockLoaded(true);
    } catch {
      toast.error('Error al cargar alertas de stock');
    } finally {
      setLoadingStock(false);
    }
  };

  useEffect(() => {
    loadData(selectedBranch);
  }, [selectedBranch, loadData]);

  const handleBranchChange = (val) => {
    const branch = val === 'ALL' ? '' : val;
    setSelectedBranch(branch);
    localStorage.setItem('dashboard_branch', branch);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-xl text-slate-500" style={{ fontFamily: 'Outfit' }}>Cargando...</div>
      </div>
    );
  }

  const ordersByStatus = stats?.orders_by_status || {};
  const lastSales = stats?.last_sales || [];

  return (
    <div className="p-6 md:p-8 space-y-6" data-testid="dashboard-page">

      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Outfit' }}>Dashboard</h1>
          <p className="text-slate-500 text-sm mt-1">
            {isAdmin
            ? (!selectedBranch ? 'Todas las sucursales' : branches.find(b => b.id === selectedBranch)?.name || '')
            : (branches.find(b => b.id === user?.branch_id)?.name || '')}
          </p>
        </div>

        {/* Branch selector — admin only */}
        {isAdmin && (
          <div className="flex items-center gap-2">
            <Building2 className="w-4 h-4 text-slate-400" />
            <Select value={selectedBranch || 'ALL'} onValueChange={handleBranchChange}>
              <SelectTrigger className="w-56">
                <SelectValue placeholder="Todas las sucursales" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="ALL">Todas las sucursales</SelectItem>
                {branches.map(b => (
                  <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}
      </div>

      {/* KPIs principales */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatCard
          title="Ventas de Hoy"
          value={money(stats?.today_sales)}
          subtitle={`${stats?.today_invoices_count || 0} facturas`}
          icon={DollarSign}
          testId="stat-card-today-sales"
          compact
        />
        <StatCard
          title="Ventas del Mes"
          value={money(stats?.month_sales)}
          subtitle="Mes actual"
          icon={Receipt}
          testId="stat-card-month-sales"
          compact
        />
        <StatCard
          title="Pedidos Activos"
          value={stats?.active_orders_count || 0}
          subtitle={stats?.overdue_orders_count > 0 ? `⚠️ ${stats.overdue_orders_count} vencido(s)` : 'Al día'}
          icon={Scissors}
          iconColor={stats?.overdue_orders_count > 0 ? 'text-rose-500' : 'text-cyan-500'}
          valueColor={stats?.overdue_orders_count > 0 ? 'text-rose-600' : ''}
          testId="stat-card-active-orders"
        />
        <StatCard
          title="Alertas de Stock"
          value={lowStockLoaded ? lowStockAlerts.length : '—'}
          subtitle={
            lowStockLoaded
              ? (lowStockAlerts.length > 0 ? 'Productos bajos' : 'Todo en orden')
              : <button onClick={handleLoadStock} disabled={loadingStock}
                  className="text-xs text-cyan-600 hover:underline disabled:opacity-50">
                  {loadingStock ? 'Consultando...' : 'Consultar ahora'}
                </button>
          }
          icon={AlertTriangle}
          iconColor="text-rose-500"
          valueColor={lowStockLoaded && lowStockAlerts.length > 0 ? 'text-rose-500' : ''}
          testId="stat-card-low-stock"
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">

        {/* Pedidos por estado */}
        <Card className="bg-white border border-slate-200 shadow-sm">
          <CardHeader>
            <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'Outfit' }}>
              <Scissors className="w-5 h-5 text-cyan-500" />
              Pedidos Activos por Estado
            </CardTitle>
          </CardHeader>
          <CardContent>
            {Object.keys(ordersByStatus).length > 0 ? (
              <div className="space-y-3">
                {Object.entries(ordersByStatus).map(([status, count]) => (
                  <div key={status} className="flex items-center justify-between">
                    <span className={`inline-block rounded-full px-3 py-1 text-xs font-semibold ${STATUS_COLORS[status] || 'bg-slate-100 text-slate-600'}`}>
                      {STATUS_LABELS[status] || status}
                    </span>
                    <span className="font-bold text-slate-700" style={{ fontFamily: 'JetBrains Mono' }}>{count}</span>
                  </div>
                ))}
              </div>
            ) : (
              <div className="h-32 flex items-center justify-center text-slate-400 text-sm">
                No hay pedidos activos
              </div>
            )}
          </CardContent>
        </Card>

        {/* Últimas ventas del día */}
        <Card className="bg-white border border-slate-200 shadow-sm">
          <CardHeader>
            <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'Outfit' }}>
              <Clock className="w-5 h-5 text-cyan-500" />
              Últimas Ventas del Día
            </CardTitle>
          </CardHeader>
          <CardContent>
            {lastSales.length > 0 ? (
              <div className="space-y-2">
                {lastSales.map((sale, i) => (
                  <div key={i} className="flex items-center justify-between py-2 border-b border-slate-100 last:border-0">
                    <div>
                      <p className="text-sm font-medium text-slate-700 capitalize">{sale.customer_name || 'Consumidor Final'}</p>
                      <p className="text-xs text-slate-400">{sale.invoice_number} · {sale.payment_method}</p>
                    </div>
                    <span className="font-bold text-cyan-600 text-sm" style={{ fontFamily: 'JetBrains Mono' }}>
                      {money(sale.total)}
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <div className="h-32 flex items-center justify-center text-slate-400 text-sm">
                No hay ventas hoy
              </div>
            )}
          </CardContent>
        </Card>

      </div>

      {/* Gráfica ventas semana */}
      <Card className="bg-white border border-slate-200 shadow-sm">
        <CardHeader>
          <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'Outfit' }}>
            <Receipt className="w-5 h-5 text-cyan-500" />
            Ventas de la Semana
          </CardTitle>
        </CardHeader>
        <CardContent>
          {(stats?.week_sales || []).some(d => d.ventas > 0) ? (
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={stats?.week_sales || []} margin={{ top: 5, right: 10, left: 10, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="day" stroke="#94a3b8" tick={{ fontSize: 12 }} />
                <YAxis stroke="#94a3b8" tick={{ fontSize: 12 }}
                  tickFormatter={(v) => `RD$${(v/1000).toFixed(0)}k`} />
                <Tooltip
                  formatter={(v) => [`RD$${parseFloat(v).toLocaleString('es-DO', {minimumFractionDigits: 2})}`, 'Ventas']}
                  contentStyle={{ borderRadius: '8px', border: '1px solid #e2e8f0' }}
                />
                <Bar dataKey="ventas" fill="#06B6D4" radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-48 flex items-center justify-center text-slate-400 text-sm">
              No hay ventas esta semana
            </div>
          )}
        </CardContent>
      </Card>

      {/* Ventas por categoría del mes */}
      {(stats?.sales_by_category || []).length > 0 && (
        <Card className="bg-white border border-slate-200 shadow-sm">
          <CardHeader>
            <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'Outfit' }}>
              <Tag className="w-5 h-5 text-cyan-500" />
              Ventas por Categoría — Mes Actual
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={Math.max(180, (stats.sales_by_category.length * 42))}>
              <BarChart
                data={stats.sales_by_category}
                layout="vertical"
                margin={{ top: 4, right: 60, left: 10, bottom: 4 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" horizontal={false} />
                <XAxis type="number" stroke="#94a3b8" tick={{ fontSize: 11 }}
                  tickFormatter={v => `RD$${(v / 1000).toFixed(0)}k`} />
                <YAxis type="category" dataKey="category" stroke="#94a3b8"
                  tick={{ fontSize: 12 }} width={120} />
                <Tooltip
                  formatter={v => [`RD$${parseFloat(v).toLocaleString('es-DO', { minimumFractionDigits: 2 })}`, 'Ventas']}
                  contentStyle={{ borderRadius: '8px', border: '1px solid #e2e8f0' }}
                />
                <Bar dataKey="total" radius={[0, 6, 6, 0]} label={{ position: 'right', fontSize: 11, fill: '#64748b', formatter: v => `RD$${(v/1000).toFixed(1)}k` }}>
                  {stats.sales_by_category.map((_, i) => (
                    <Cell key={i} fill={['#06B6D4','#0EA5E9','#6366F1','#8B5CF6','#EC4899','#F59E0B','#10B981','#EF4444'][i % 8]} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      )}

      {/* Stock bajo */}
      <Card className="bg-white border border-slate-200 shadow-sm" data-testid="low-stock-alerts">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'Outfit' }}>
              <AlertTriangle className="w-5 h-5 text-rose-500" />
              Alertas de Stock Bajo
            </CardTitle>
            <button onClick={handleLoadStock} disabled={loadingStock}
              className="text-xs text-cyan-600 hover:underline disabled:opacity-50 flex items-center gap-1">
              {loadingStock ? 'Consultando...' : lowStockLoaded ? '↻ Actualizar' : 'Consultar'}
            </button>
          </div>
        </CardHeader>
        <CardContent>
          {!lowStockLoaded ? (
            <p className="text-sm text-slate-400 text-center py-4">Haz clic en "Consultar" para ver los productos con stock bajo.</p>
          ) : lowStockAlerts.length === 0 ? (
            <p className="text-sm text-emerald-600 text-center py-4">✓ Todos los productos tienen stock suficiente.</p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 max-h-64 overflow-y-auto">
              {lowStockAlerts.map((alert, i) => (
                <div key={i} className="flex items-center justify-between p-3 bg-rose-50 border border-rose-200 rounded-lg">
                  <div>
                    <p className="font-medium text-slate-900 text-sm">{alert.product_name}</p>
                    <p className="text-xs text-slate-500">{alert.size} - {alert.color}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-bold text-rose-600">{alert.current_stock} uds</p>
                    <p className="text-xs text-slate-400">Mín: {alert.min_stock}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

    </div>
  );
};

export default Dashboard;